/**
 * Tools for the trace-scoped chat in the lanes dock.
 *
 * Every one of these reads the model the panel ALREADY built for the open
 * trace - the lanes, the turns, and the reading report - through a single
 * `getModel` thunk, the same idiom the kernel editor's tools use for its
 * draft. That is deliberate and it is the whole cost story: the chat asks
 * questions about a trace the reader is already looking at, so it must not
 * issue a second read of anything. No fetches, no queries, no re-derivation.
 *
 * They never write the READING. A person's assertion about a reading is worth
 * capturing, but it can only become a verdict once ops carry the drafter that
 * produced them; until then a "dispute this" tool would collect testimony that
 * cannot be credited or debited to anything, so it does not exist yet. The one
 * tool that writes, `trace_annotate`, writes the reader's own view: notes on
 * the lanes at the turns an answer points at, held with the conversation.
 *
 * Every result is bounded. A trace can carry tens of thousands of turns and
 * a lane per file it touched, and an unbounded lane dump would spend the
 * context window on rows nobody asked about.
 *
 * The lane tools answer with the shape of the work; `trace_read` and
 * `trace_search` answer with its content - what was asked, said, run and
 * returned - read through `readTurn`, the same reading the Lanes turn dock
 * shows. Content is paged rather than dumped: a clipped block says where to
 * read on, so a tool result of tens of kilobytes is reachable without any one
 * answer paying for all of it.
 *
 * Turn numbers go in and out as the axis PRINTS them. A long conversation
 * loads its newest page first and every printed number adds the messages
 * before that page (`turnBase`), so an index into the loaded turns names a
 * different turn from the one the reader finds under that number - and the
 * index shifts again when an earlier page loads, which would quietly repoint
 * every turn an earlier answer named.
 *
 * A lookup that cannot be answered (no such lane, a turn off the axis) throws.
 * The agent loop hands the message back to the model as an error result, and
 * the answer's trail marks the row as failed rather than drawing it as a
 * lookup that worked.
 */

import { Type } from "@earendil-works/pi-ai";
import { readingRows, readingDockTotals } from "./traceReading.js";
import { readTurn } from "./traceToolCall.js";
import { LOOP_BREAKER_TAG } from "./loopBreaker.js";

// Caps, in rows. Chosen so the widest answer stays a few thousand tokens:
// past that the model is being handed a haystack rather than an answer, and
// the narrowing arguments (domain, contains, turn) are the way through.
const LANE_CAP = 24;
const ITEM_CAP = 24;
const READING_CAP = 16;
const TURN_CALL_CAP = 24;

// Content caps, in characters. One read stays near 3k tokens however long the
// turns are; a single turn gets room per block that a run of turns shares out.
const READ_SPAN = 6;
const READ_BLOCK_CAP = 4000;
const READ_RANGE_BLOCK_CAP = 1200;
const READ_PAGE = 8000;
const READ_BUDGET = 12000;
// Below this much budget, starting another block would show a stub of it.
const READ_MIN_ROOM = 200;
const SEARCH_HITS = 20;
const SNIPPET_SIDE = 70;

// Messages before the loaded page: what every printed turn number adds.
function baseOf(m) {
  const base = Number(m?.turnBase);
  return Number.isFinite(base) && base > 0 ? base : 0;
}

function turnCountOf(m) {
  return m?.lanes?.turnCount ?? (Array.isArray(m?.turns) ? m.turns.length : 0);
}

function axisSpan(m) {
  const base = baseOf(m);
  const count = turnCountOf(m);
  return count ? `T${base}-T${base + count - 1}` : "no turns";
}

// A turn number as the axis prints it, as an index into the loaded turns.
function turnIndexOf(m, printed, argName) {
  const n = Number(printed);
  const local = n - baseOf(m);
  if (!Number.isInteger(n) || local < 0 || local >= turnCountOf(m)) {
    throw new Error(`${argName} ${printed} is not on this trace's axis, which runs ${axisSpan(m)}.`);
  }
  return local;
}

// A lane in the words the rail uses, minus the geometry. `first` and `last`
// are the turns that touched the subject, which is what makes a lane
// answerable ("when did it come back to this file") without any cell detail.
function laneLine(lane, base) {
  const turns = lane.turns || [];
  return {
    id: lane.id,
    label: lane.label || lane.id,
    domain: lane.domain || null,
    path: lane.path || null,
    touches: turns.length,
    first: turns.length ? base + turns[0] : null,
    last: turns.length ? base + turns[turns.length - 1] : null,
    digest: lane.digest || null,
  };
}

// One mark's worth of what happened, without the payload. The payload is the
// expensive half; a question that needs it reads the turn with trace_read.
function itemLine(item) {
  return {
    turn: item.turn ?? null,
    kind: item.kind || item.op?.kind || null,
    subject: item.subject || item.op?.subject || null,
    tool: item.name || item.op?.name || null,
    // A failed op carries `ok: false`; there is no `failed` flag on an item
    // or an op anywhere in the lanes model. Reading one meant this tool could
    // never report a failure the plane draws in red, which is exactly the
    // disagreement between the screen and the answer that the chat's prompt
    // promises cannot happen. Same test every other consumer uses.
    failed: item.op?.ok === false,
    tier: item.op?.readByModel ? "verified (model)" : item.op ? "extractor" : null,
  };
}

function textResult(text, details) {
  return { content: [{ type: "text", text }], details };
}

// One line per row. Fields are separated by two spaces so a model can read
// them positionally without the shape of JSON around every value.
function joinLines(rows) {
  return rows.join("\n");
}

function laneRowLine(l) {
  const span = l.first == null ? "no turns" : l.first === l.last ? `T${l.first}` : `T${l.first}-T${l.last}`;
  return [l.id, l.label, l.domain || "unclassified", `${l.touches} touch${l.touches === 1 ? "" : "es"}`, span]
    .join("  ");
}

function itemRowLine(it) {
  return [`T${it.turn}`, it.kind || "?", it.subject || "-", it.tool || "-", it.failed ? "FAILED" : null, it.tier]
    .filter(Boolean).join("  ");
}

function noTrace() {
  return textResult("No trace is open in this panel.", { noTrace: true });
}

// A lane by id, or by a label that picks out exactly one lane: a model told to
// name lanes the way the rail does will sometimes pass that name back.
function findLane(m, ref) {
  const lanes = m.lanes.lanes || [];
  const byId = m.lanes.laneById?.get(ref) || lanes.find((l) => l.id === ref);
  if (byId) return byId;
  const named = lanes.filter((l) => l.label === ref);
  if (named.length === 1) return named[0];
  if (named.length > 1) {
    throw new Error(`${named.length} lanes are named ${ref}: ${named.slice(0, 6).map((l) => l.id).join(", ")}. Pass one of those ids.`);
  }
  throw new Error(`No lane has the id ${ref}. Call trace_lanes to see what exists.`);
}

/**
 * The orientation tool. Answers "what am I looking at" in one call so the
 * model does not start every conversation by listing lanes.
 */
export function createTraceOverviewTool({ getModel }) {
  return {
    name: "trace_overview",
    label: "Trace overview",
    description:
      "The shape of the trace open in this panel: its turns as the axis numbers them, how many lanes exist per domain, how much of the tool traffic the extractors claimed, and whether a model reading has been run. Call this FIRST when you need orientation - it is one call and it usually removes the need to list lanes at all.",
    parameters: Type.Object({}),
    async execute() {
      const m = getModel();
      if (!m?.lanes) return noTrace();
      const byDomain = new Map();
      for (const lane of m.lanes.lanes || []) {
        const d = lane.domain || "unclassified";
        byDomain.set(d, (byDomain.get(d) || 0) + 1);
      }
      const domains = [...byDomain.entries()].sort((a, b) => b[1] - a[1]).map(([d, n]) => `${d}: ${n}`);
      const base = baseOf(m);
      const turnCount = turnCountOf(m);
      const laneCount = (m.lanes.lanes || []).length;
      const cov = m.lanes.coverage || {};
      const report = m.reading?.report || null;
      const totals = report ? readingDockTotals(report) : null;
      const account = m.reading?.summary?.summary || null;
      const out = [
        `turns: ${turnCount}, numbered ${axisSpan(m)} as the axis prints them${
          base ? `; the ${base} before T${base} are not loaded in this panel` : ""
        }`,
        `lanes: ${laneCount} (${domains.join(", ") || "none"})`,
        `tool calls: ${cov.toolUses ?? 0}, claimed by extractors: ${cov.claimed ?? 0}, residual: ${cov.residual ?? 0}`,
        totals
          ? `reading: ${totals.calls} calls read, ${totals.accepted} claims kept, ${totals.rejected} disputed, ${totals.additions} additions`
          : `reading: not run on this trace`,
      ];
      if (account?.outcome) {
        out.push("", "what this agent did (the reading's own account):", account.outcome);
        for (const step of account.steps || []) {
          out.push(`  - ${typeof step === "string" ? step : step?.text || step?.line || ""}`.trimEnd());
        }
      }
      return textResult(joinLines(out), {
        turnCount, laneCount, turnBase: base, domains: [...byDomain], coverage: cov, totals, account,
      });
    },
  };
}

/**
 * The lane index. Narrowed rather than paged: a reader asking a question about
 * a trace almost always has a domain or a name fragment in mind, and offering
 * a cursor instead would invite walking the whole set one page at a time.
 */
export function createTraceLanesTool({ getModel }) {
  return {
    name: "trace_lanes",
    label: "List lanes",
    description:
      `The subjects this trace touched, as lanes. Narrow with 'domain' (files, shell, vcs, build, research, query, ...) or 'contains' (matches the label or path) - the result is capped at ${LANE_CAP} lanes, so a bare call on a large trace tells you the shape rather than the whole list. Each row is: id, label (the name the rail prints), domain, touches, and the turns that touched it, which is how you answer "when" questions without asking for content.`,
    parameters: Type.Object({
      domain: Type.Optional(Type.String({ description: "Only lanes in this domain." })),
      contains: Type.Optional(Type.String({ description: "Case-insensitive substring of the lane's label or path." })),
    }),
    async execute(_toolCallId, { domain = null, contains = null } = {}) {
      const m = getModel();
      if (!m?.lanes) return noTrace();
      const base = baseOf(m);
      const needle = contains ? String(contains).toLowerCase() : null;
      const all = (m.lanes.lanes || []).filter((lane) => {
        if (domain && (lane.domain || "unclassified") !== domain) return false;
        if (!needle) return true;
        return `${lane.label || ""} ${lane.path || ""} ${lane.id}`.toLowerCase().includes(needle);
      });
      const rows = all.slice(0, LANE_CAP).map((lane) => laneLine(lane, base));
      const head = all.length > rows.length
        ? `${all.length} lanes match; showing ${rows.length}. Narrow with domain or contains -- re-running this call unchanged returns the same ${rows.length}.`
        : `${all.length} lane${all.length === 1 ? "" : "s"} match.`;
      return textResult(`${head}\n${joinLines(rows.map(laneRowLine))}`, { total: all.length, lanes: rows });
    },
  };
}

/**
 * One lane's work, optionally as of a turn. The "as of" is the same prefix
 * rule the dock replays: a lane read at turn N shows what the agent could
 * have known by N, so an answer about what it knew cannot quote a later mark.
 */
export function createTraceLaneTool({ getModel }) {
  return {
    name: "trace_lane",
    label: "Read a lane",
    description:
      `What happened on one lane, in order. Pass 'as_of' to cut the lane at a turn - that is the prefix the panel replays, so it is the honest answer to "what did the agent know at this point"; a mark after 'as_of' had not happened yet. Capped at ${ITEM_CAP} items.`,
    parameters: Type.Object({
      lane_id: Type.String({ description: "The lane's id, from trace_lanes." }),
      as_of: Type.Optional(Type.Number({ description: "Turn number to cut the lane at, inclusive, as the axis prints it. Omit for the whole lane." })),
    }),
    async execute(_toolCallId, { lane_id, as_of = null } = {}) {
      const m = getModel();
      if (!m?.lanes) return noTrace();
      if (!lane_id) {
        throw new Error("trace_lane needs a lane_id. Call trace_lanes first and pass one of the ids it returns.");
      }
      const lane = findLane(m, lane_id);
      const base = baseOf(m);
      const cut = as_of == null ? null : turnIndexOf(m, as_of, "as_of");
      // `lane.turns` is sorted ascending by the producer and may repeat a turn
      // once per item on it, so walking it in order needs no sort -- and the
      // dedupe keeps a repeated turn from counting its cell twice.
      const shown = [];
      let total = 0;
      let prev = null;
      for (const turn of lane.turns || []) {
        if (turn === prev) continue;
        prev = turn;
        if (cut != null && turn > cut) break;
        const list = lane.cells?.get?.(turn) || [];
        total += list.length;
        for (const it of list) {
          if (shown.length >= ITEM_CAP) break;
          shown.push({ ...itemLine(it), turn: base + turn });
        }
      }
      const line = laneLine(lane, base);
      const head = [
        `${line.label} (${lane.id})`,
        cut != null ? `as of T${base + cut}` : "whole lane",
        total > shown.length ? `${total} items, showing the first ${shown.length}` : `${total} item${total === 1 ? "" : "s"}`,
      ].join(" - ");
      return textResult(`${head}\n${joinLines(shown.map(itemRowLine))}`, { lane: line, items: shown, total });
    },
  };
}

/**
 * The reading's verdicts. Kept separate from the lanes because a lane says
 * what the drafters placed and this says what the judge made of it - the
 * disputes are the interesting rows, so they sort first.
 */
export function createTraceReadingTool({ getModel }) {
  return {
    name: "trace_reading",
    label: "Read the reading",
    description:
      `What the model reading made of this trace: per call, which drafted claims it kept, which it disputed and why, and what it added that the drafters missed. Disputed calls come first because they are where the drafters and the judge disagree. Capped at ${READING_CAP} calls. Returns nothing useful if no reading has been run - check trace_overview first.`,
    parameters: Type.Object({
      only_disputed: Type.Optional(Type.Boolean({ description: "Only calls with at least one disputed claim or one addition." })),
    }),
    async execute(_toolCallId, { only_disputed = false } = {}) {
      const m = getModel();
      const report = m?.reading?.report || null;
      if (!report) {
        return textResult("No reading has been run on this trace, so there are no verdicts to read.", { empty: true });
      }
      const base = baseOf(m);
      const all = readingRows(report).filter((r) => (only_disputed ? r.rejected > 0 || (r.additions || []).length > 0 : true));
      // Contested first: a kept claim confirms what the lane already shows,
      // a disputed one is the only row that says the two disagree.
      const ranked = [...all].sort((a, b) => (b.rejected + (b.additions || []).length) - (a.rejected + (a.additions || []).length));
      const shown = ranked.slice(0, READING_CAP)
        .map((r) => ({ ...r, turn: r.turn == null ? null : `T${base + r.turn}` }));
      const totals = readingDockTotals(report);
      const head = `${totals.calls} calls read, ${totals.accepted} kept, ${totals.rejected} disputed, ${totals.additions} additions.${
        ranked.length > shown.length ? ` ${ranked.length} rows match; showing the first ${shown.length}.` : ""
      }`;
      return textResult(`${head}\n${JSON.stringify(shown, null, 1)}`, { totals, rows: shown, total: ranked.length });
    },
  };
}

/**
 * One turn's tool calls. The turn axis is the reader's other coordinate - a
 * question about a lane is a question about a subject, and this is the same
 * question asked about a moment.
 */
export function createTraceTurnTool({ getModel }) {
  return {
    name: "trace_turn",
    label: "Read a turn",
    description:
      `Which lanes this turn put work on, and the kind of work - the structure of a moment, not its content. Use it to widen from one lane to the rest of a turn; for what the turn actually said, ran or returned, use trace_read. Capped at ${TURN_CALL_CAP} items.`,
    parameters: Type.Object({
      turn: Type.Number({ description: "Turn number, as the axis prints it (T12 is 12)." }),
    }),
    async execute(_toolCallId, { turn } = {}) {
      const m = getModel();
      if (!m?.lanes) return noTrace();
      const t = turnIndexOf(m, turn, "Turn");
      const printed = baseOf(m) + t;
      const items = [];
      for (const lane of m.lanes.lanes || []) {
        for (const it of lane.cells?.get?.(t) || []) {
          items.push({ lane: lane.id, ...itemLine(it), turn: printed });
        }
      }
      const shown = items.slice(0, TURN_CALL_CAP);
      if (!items.length) {
        return textResult(`T${printed} put no work on any lane. It may be a prompt, a reply, or a call no extractor claimed.`, { turn: printed, items: [], total: 0, lanes: 0 });
      }
      const head = items.length > shown.length
        ? `T${printed}: ${items.length} items, showing the first ${shown.length}`
        : `T${printed}: ${items.length} item${items.length === 1 ? "" : "s"}`;
      return textResult(`${head}\n${joinLines(shown.map((it) => `${it.lane}  ${itemRowLine(it)}`))}`, {
        turn: printed, items: shown, total: items.length, lanes: new Set(items.map((it) => it.lane)).size,
      });
    },
  };
}

// One block of a turn as numbered lines: its label, what it holds from `from`,
// and where to read on when it did not fit.
function blockLines(printed, index, block, from, limit) {
  const full = block.text || "";
  const shown = full.slice(from, from + limit);
  const lines = [`[${index}] ${block.label}${block.failed ? " (FAILED)" : ""}:`, shown || "(empty)"];
  const rest = full.length - (from + shown.length);
  if (rest > 0) {
    lines.push(`[clipped: ${rest} more chars - read on with turn=${printed} block=${index} offset=${from + shown.length}]`);
  }
  return { lines, shown: shown.length, clipped: rest > 0 };
}

/**
 * What a turn actually holds: the prompt or reply text, each tool call with its
 * arguments, and what the call returned.
 */
export function createTraceReadTool({ getModel }) {
  return {
    name: "trace_read",
    label: "Read turns",
    description:
      `The content of a turn as the Lanes dock shows it: what the user asked, what the agent said, each tool call with its arguments, and what the call returned. This is how you answer what a turn actually SAID or DID; trace_turn only says which lanes it touched. Pass 'through' to read a short run of turns (at most ${READ_SPAN}). Blocks are numbered; a block too long to show whole is clipped and says how to read on with 'block' and 'offset'.`,
    parameters: Type.Object({
      turn: Type.Number({ description: "Turn number as the axis prints it (T12 is 12)." }),
      through: Type.Optional(Type.Number({ description: `Last turn to read, inclusive, as the axis prints it; at most ${READ_SPAN - 1} past 'turn'.` })),
      block: Type.Optional(Type.Number({ description: "Start at this block of 'turn', by the number trace_read printed. Only with a single turn." })),
      offset: Type.Optional(Type.Number({ description: "Character offset into 'block' to start from." })),
    }),
    async execute(_toolCallId, { turn, through = null, block = null, offset = null } = {}) {
      const m = getModel();
      if (!m?.lanes) return noTrace();
      const base = baseOf(m);
      const first = turnIndexOf(m, turn, "Turn");
      const asked = through == null ? first : turnIndexOf(m, through, "through");
      if (asked < first) throw new Error(`through ${through} is before turn ${turn}.`);
      if ((block != null || offset != null) && asked !== first) {
        throw new Error("block and offset read within one turn, so they cannot be combined with through.");
      }
      if (offset != null && block == null) {
        throw new Error("offset needs a block: pass the block number trace_read printed.");
      }
      const last = Math.min(asked, first + READ_SPAN - 1);
      const cap = last > first ? READ_RANGE_BLOCK_CAP : READ_BLOCK_CAP;
      const out = [];
      let used = 0;
      let blocks = 0;
      let clipped = 0;
      let stopped = null;
      for (let t = first; t <= last && !stopped; t++) {
        const printed = base + t;
        if (READ_BUDGET - used < READ_MIN_ROOM) {
          stopped = { turn: printed, block: 0 };
          break;
        }
        const read = readTurn(m.turns, t, Infinity);
        const list = read?.blocks || [];
        out.push(`T${printed} ${read?.role || "?"}`);
        if (!list.length) {
          out.push("(this turn carries no content)");
          continue;
        }
        let start = 0;
        let from = 0;
        if (t === first && block != null) {
          start = Number(block);
          if (!Number.isInteger(start) || start < 0 || start >= list.length) {
            throw new Error(`T${printed} has blocks 0-${list.length - 1}; there is no block ${block}.`);
          }
          from = offset == null ? 0 : Number(offset);
          const len = (list[start].text || "").length;
          if (!Number.isInteger(from) || from < 0 || (from > 0 && from >= len)) {
            throw new Error(`offset ${offset} is outside T${printed} block ${start}, which is ${len} chars.`);
          }
        }
        for (let b = start; b < list.length; b++) {
          const room = READ_BUDGET - used;
          if (room < READ_MIN_ROOM) {
            stopped = { turn: printed, block: b };
            break;
          }
          const paging = block != null && t === first && b === start;
          const got = blockLines(printed, b, list[b], paging ? from : 0, Math.min(paging ? READ_PAGE : cap, room));
          out.push(...got.lines);
          used += got.shown;
          blocks += 1;
          if (got.clipped) clipped += 1;
        }
      }
      if (stopped) {
        out.push(`[stopped at the read budget - continue with turn=${stopped.turn} block=${stopped.block}]`);
      } else if (last < asked) {
        out.push(`[read T${base + first}-T${base + last} of the T${base + first}-T${base + asked} asked for - continue with turn=${base + last + 1}]`);
      }
      return textResult(joinLines(out), {
        from: base + first, through: base + last, blocks, chars: used, clipped, stopped,
      });
    },
  };
}

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function snippetAt(text, at, len) {
  const start = Math.max(0, at - SNIPPET_SIDE);
  const end = Math.min(text.length, at + len + SNIPPET_SIDE);
  const body = text.slice(start, end).replace(/\s+/g, " ").trim();
  return `${start > 0 ? "..." : ""}${body}${end < text.length ? "..." : ""}`;
}

/**
 * Where something appears in the trace's content. One hit per block, so a long
 * output that repeats a word is one row that says how often rather than a page
 * of rows from the same block.
 */
export function createTraceSearchTool({ getModel }) {
  return {
    name: "trace_search",
    label: "Search the content",
    description:
      `Find the turns whose content contains some text - prompts, replies, tool call arguments and tool results - matched case-insensitively. Each hit names its turn and block with the text around the match, so you can trace_read what it points at. Returns at most ${SEARCH_HITS} hits, earliest first.`,
    parameters: Type.Object({
      query: Type.String({ description: "Text to look for, matched case-insensitively as a substring." }),
    }),
    async execute(_toolCallId, { query } = {}) {
      const m = getModel();
      if (!m?.lanes) return noTrace();
      const wanted = String(query ?? "").trim();
      if (!wanted) throw new Error("trace_search needs some text to look for.");
      const base = baseOf(m);
      const count = turnCountOf(m);
      const every = new RegExp(escapeRegExp(wanted), "gi");
      const firstOf = new RegExp(escapeRegExp(wanted), "i");
      const hits = [];
      const turnsHit = new Set();
      let total = 0;
      for (let t = 0; t < count; t++) {
        const read = readTurn(m.turns, t, Infinity);
        (read?.blocks || []).forEach((blk, b) => {
          const text = blk.text || "";
          const found = text.match(every);
          if (!found) return;
          total += 1;
          turnsHit.add(t);
          if (hits.length >= SEARCH_HITS) return;
          const at = text.search(firstOf);
          hits.push({
            turn: base + t,
            role: read.role,
            block: b,
            label: blk.label,
            failed: !!blk.failed,
            matches: found.length,
            snippet: snippetAt(text, at, wanted.length),
          });
        });
      }
      if (!total) {
        return textResult(`No turn's content contains "${wanted}".`, { query: wanted, total: 0, turns: 0, hits: [] });
      }
      const head = `${total} block${total === 1 ? "" : "s"} in ${turnsHit.size} turn${turnsHit.size === 1 ? "" : "s"} contain "${wanted}"${
        total > hits.length ? `; showing the first ${hits.length}` : ""
      }.`;
      const rows = hits.map((h) => [
        `T${h.turn}`,
        h.role,
        `[${h.block}] ${h.label}${h.failed ? " (FAILED)" : ""}${h.matches > 1 ? ` (${h.matches} matches)` : ""}:`,
        h.snippet,
      ].join("  "));
      return textResult(`${head}\n${joinLines(rows)}`, { query: wanted, total, turns: turnsHit.size, hits });
    },
  };
}

// A note says what happened at a turn; the answer explains it. Past this a
// note is becoming the answer, and a pin's card is the wrong place for one.
const NOTE_CHARS = 160;
const NOTES_PER_CALL = 24;

/**
 * Notes pinned on the lanes at the turns an answer points at, on one lane's
 * mark or on the turn, through `annotate`, which holds them with the
 * conversation and returns them as held. A note that cannot be placed is
 * refused on its own and the rest are pinned; a lane with no mark at the turn
 * puts its note on the turn, and the result says so. A note with no lane goes
 * on the turn's mark when exactly one lane has one there: that mark is what
 * the turn did, and a pin on it sits where the reader looks.
 */
export function createTraceAnnotateTool({ getModel, annotate }) {
  return {
    name: "trace_annotate",
    label: "Pin notes on the lanes",
    description:
      `Pin short notes on the reader's lanes at the turns your answer points at, so they can see where on the axis, not just read it. A note goes on one lane's mark at a turn (pass the lane), or, with no lane, on the turn's one mark when it has only one and on the turn itself otherwise. Say what happened there in a few words; the answer explains. Up to ${NOTES_PER_CALL} notes a call, each under ${NOTE_CHARS} characters. They stay with this conversation.`,
    parameters: Type.Object({
      notes: Type.Array(Type.Object({
        turn: Type.Number({ description: "Turn number, as the axis prints it (T12 is 12)." }),
        lane: Type.Optional(Type.String({ description: "The lane's id from trace_lanes, or its label when that names one lane, to pin the note on that lane's mark at this turn. Omit to pin it on the turn." })),
        note: Type.String({ description: "What happened there, in a few words." }),
      }), { description: "The notes to pin.", minItems: 1, maxItems: NOTES_PER_CALL }),
    }),
    async execute(_toolCallId, { notes = [] } = {}) {
      const m = getModel();
      if (!m?.lanes) return noTrace();
      if (!Array.isArray(notes) || !notes.length) {
        throw new Error("trace_annotate needs at least one note: a turn and what happened there, and optionally a lane.");
      }
      const base = baseOf(m);
      const pinned = [];
      const refused = [];
      for (const n of notes.slice(0, NOTES_PER_CALL)) {
        const text = String(n?.note ?? "").replace(/\s+/g, " ").trim();
        if (!text) {
          refused.push({ turn: n?.turn ?? null, why: "the note is empty" });
          continue;
        }
        let t;
        try {
          t = turnIndexOf(m, n.turn, "Turn");
        } catch (err) {
          refused.push({ turn: n?.turn ?? null, why: firstSentence(err.message) });
          continue;
        }
        let lane = null;
        if (n.lane != null && String(n.lane).trim()) {
          try {
            lane = findLane(m, String(n.lane).trim());
          } catch (err) {
            refused.push({ turn: base + t, why: firstSentence(err.message) });
            continue;
          }
        }
        const hasMark = (l) => (l.cells?.get?.(t) || []).length > 0;
        let onMark = lane ? hasMark(lane) : false;
        let chosen = false;
        if (!lane) {
          const marked = (m.lanes.lanes || []).filter(hasMark);
          if (marked.length === 1) {
            [lane] = marked;
            onMark = true;
            chosen = true;
          }
        }
        pinned.push({
          printed: base + t,
          lane: onMark ? lane.id : null,
          laneLabel: lane ? lane.label || lane.id : null,
          text: text.length > NOTE_CHARS ? `${text.slice(0, NOTE_CHARS - 3)}...` : text,
          moved: Boolean(lane) && !onMark,
          chosen,
        });
      }
      if (!pinned.length) {
        throw new Error(`No note was pinned. ${refused.map((r) => `${r.turn != null ? `T${r.turn}` : "A note"}: ${r.why}`).join(" ")}`);
      }
      const held = annotate(pinned.map(({ moved, chosen, ...note }) => note)) || [];
      const where = (p) => {
        if (p.chosen) return `${p.laneLabel} (the one lane with a mark at T${p.printed})`;
        if (p.lane) return p.laneLabel;
        return p.moved ? `on the turn (${p.laneLabel} has no mark at T${p.printed})` : "on the turn";
      };
      const lines = pinned.map((p) => `T${p.printed}  ${where(p)}  ${p.text}`);
      const notPinned = refused.length
        ? `\nNot pinned: ${refused.map((r) => `${r.turn != null ? `T${r.turn}` : "a note"} - ${r.why}`).join("; ")}`
        : "";
      // What it pinned, for the chat to show each one where it stands
      // (components/ChatPinsCard.jsx).
      const laneLabel = new Map(pinned.filter((p) => p.lane).map((p) => [p.lane, p.laneLabel]));
      const pins = held.filter((h) => h?.id).map((h) => ({ id: h.id, session: h.session || null, text: h.text, turn: h.printed ?? null, lane: h.lane ? laneLabel.get(h.lane) || h.lane : null }));
      return textResult(`Pinned ${counted(pinned.length, "note")} on the lanes.\n${joinLines(lines)}${notPinned}`, {
        pinned: pinned.length,
        onMarks: pinned.filter((p) => p.lane).length,
        refused: refused.length,
        turns: pinned.map((p) => p.printed),
        ids: pins.map((p) => p.id),
        ...(pins.length ? { kind: "boardPins", pins } : {}),
      });
    },
  };
}

// `annotate`, when given, is where notes the chat pins are held; without it
// the chat has only the read-only lookups.
export function createTraceChatTools({ getModel, annotate = null }) {
  return [
    createTraceOverviewTool({ getModel }),
    createTraceLanesTool({ getModel }),
    createTraceLaneTool({ getModel }),
    createTraceReadingTool({ getModel }),
    createTraceTurnTool({ getModel }),
    createTraceReadTool({ getModel }),
    createTraceSearchTool({ getModel }),
    ...(typeof annotate === "function" ? [createTraceAnnotateTool({ getModel, annotate })] : []),
  ];
}

const TRACE_TOOL_NAMES = new Set([
  "trace_overview", "trace_lanes", "trace_lane", "trace_reading", "trace_turn", "trace_read", "trace_search", "trace_annotate",
]);

const counted = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

// Mid-stream a call's arguments are still a JSON fragment; once the call is
// complete they are the parsed object.
function argsOf(args) {
  if (args && typeof args === "object") return args;
  if (typeof args !== "string" || !args.trim()) return {};
  try {
    const parsed = JSON.parse(args);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

// A lane id's last segment: the name to show for a lane the lookup could not
// find, so there is no label to read off it.
function laneTail(id) {
  const s = id == null ? "" : String(id);
  return s.split(/[/:]/).filter(Boolean).pop() || s;
}

// The part of an error that says what went wrong; the rest tells the model
// what to call next.
function firstSentence(text) {
  const s = String(text || "").trim();
  const hit = s.match(/^.*?[.!?](?=\s|$)/);
  return hit ? hit[0] : s;
}

function lookupSubject(name, a, d) {
  switch (name) {
    case "trace_overview":
      return "Overview";
    case "trace_lanes":
      return ["Lanes", a.domain ? `in ${a.domain}` : null, a.contains ? `matching "${a.contains}"` : null]
        .filter(Boolean).join(" ");
    case "trace_lane": {
      const label = d.lane?.label || laneTail(a.lane_id);
      return [label ? `Lane ${label}` : "Lane", a.as_of != null ? `as of T${a.as_of}` : null].filter(Boolean).join(" ");
    }
    case "trace_turn":
      return a.turn != null ? `Turn T${a.turn}` : "Turn";
    case "trace_reading":
      return a.only_disputed ? "Disputed calls in the reading" : "The reading's verdicts";
    case "trace_read": {
      if (a.turn == null) return "Read";
      if (a.block != null) return `Read T${a.turn} block ${a.block}${a.offset ? ` from char ${a.offset}` : ""}`;
      return a.through != null && a.through !== a.turn ? `Read T${a.turn}-T${a.through}` : `Read T${a.turn}`;
    }
    case "trace_search":
      return a.query ? `Search "${a.query}"` : "Search";
    case "trace_annotate": {
      const turns = (Array.isArray(a.notes) ? a.notes : []).map((n) => n?.turn).filter((t) => t != null);
      if (!turns.length) return "Pin notes";
      const named = turns.slice(0, 3).map((t) => `T${t}`).join(", ");
      return `Pin notes on ${named}${turns.length > 3 ? ` +${turns.length - 3}` : ""}`;
    }
    default:
      return name || "Lookup";
  }
}

function lookupOutcome(name, d) {
  if (d.noTrace) return "no trace open";
  switch (name) {
    case "trace_overview":
      if (d.turnCount == null) return null;
      return [counted(d.turnCount, "turn"), counted(d.laneCount ?? 0, "lane"), d.totals ? `${counted(d.totals.calls, "call")} read` : null]
        .filter(Boolean).join(", ");
    case "trace_lanes":
      if (d.total == null) return null;
      return d.total > (d.lanes?.length ?? 0) ? `${d.total} lanes, first ${d.lanes.length} shown` : counted(d.total, "lane");
    case "trace_lane":
      if (d.total == null) return null;
      return d.total > (d.items?.length ?? 0) ? `${d.total} items, first ${d.items.length} shown` : counted(d.total, "item");
    case "trace_turn":
      if (d.total == null) return null;
      return d.total ? `${counted(d.total, "item")} on ${counted(d.lanes ?? 0, "lane")}` : "no work on any lane";
    case "trace_reading":
      if (d.empty) return "no reading has been run";
      return d.total != null ? counted(d.total, "call") : null;
    case "trace_read":
      if (d.blocks == null) return null;
      return [counted(d.blocks, "block"), d.clipped ? `${d.clipped} clipped` : null, d.stopped ? "stopped at the budget" : null]
        .filter(Boolean).join(", ");
    case "trace_search":
      if (d.total == null) return null;
      if (!d.total) return "no matches";
      return `${counted(d.total, "block")} in ${counted(d.turns, "turn")}${d.total > (d.hits?.length ?? 0) ? `, first ${d.hits.length} shown` : ""}`;
    case "trace_annotate":
      if (d.pinned == null) return null;
      return [`pinned ${counted(d.pinned, "note")}`, d.refused ? `${d.refused} not pinned` : null].filter(Boolean).join(", ");
    default:
      return null;
  }
}

/**
 * One lookup as the reader sees it in an answer's trail: what the agent
 * looked at, in the rail's words, and what came back. The tools' own text is
 * written for the model - positional columns, notes on capped rows, what to
 * call next - and read as noise in a panel.
 */
export function describeTraceLookup({ name, args, details, status, result, terminated } = {}) {
  // Until the call's name has streamed in, the fold holds it as a bare "tool".
  if (status === "preparing" && (!name || name === "tool")) return { what: "Lookup", outcome: null, failed: false };
  const a = argsOf(args);
  const d = details || {};
  const what = lookupSubject(name, a, d);
  if (status === "error") return { what, outcome: firstSentence(result) || "failed", failed: true };
  if (status !== "done") return { what, outcome: null, failed: false };
  // The loop breaker keeps the lookup's own details but replaces what the
  // model read with its stop, so the details describe a result the model
  // never saw.
  if (terminated && String(result || "").startsWith(LOOP_BREAKER_TAG)) {
    return { what, outcome: "repeated; result withheld", failed: false };
  }
  // The chat carries the main chat's built-in tools too, whose results this
  // cannot read; their own first line is the best short account there is.
  const outcome = TRACE_TOOL_NAMES.has(name)
    ? lookupOutcome(name, d)
    : String(result || "").split("\n", 1)[0].trim() || null;
  return { what, outcome, failed: false };
}
